import React, { useState } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, 
  Select, MenuItem, Box, Menu, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCategoryColor } from '../constants/categoryColors';

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function TransactionTable({ transactions, onEdit, onDelete, onRecategorize }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  
  const handleCategoryClick = (event, tx) => {
    setAnchorEl(event.currentTarget);
    setSelectedTx(tx);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedTx(null);
  };

  const handleCategoryChange = async (newCategory) => {
    if (!selectedTx || !onRecategorize) return;
    await onRecategorize(selectedTx.id, newCategory);
    handleClose();
  };

  // Get all unique categories from transactions for the dropdown
  const allCategories = [...new Set(transactions.map(tx => tx.category).filter(Boolean))].sort();
  
  const CATEGORIES = [
    'Income', 'Transfer', 'Credit Card Payment', 'Savings', 'Family Support',
    'Charity/Donation', 'Food & Dining', 'Groceries', 'Transport', 'Subscriptions',
    'Utilities', 'Healthcare', 'Shopping', 'Housing', 'Entertainment', 'Travel',
    'Fees', 'Personal Care', 'Insurance', 'Education', 'Other', 'Uncategorized'
  ];

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx, idx) => (
              <TableRow key={tx.id} hover
                sx={{
                  opacity: tx.is_reimbursed ? 0.5 : 1,
                }}
              >
                <TableCell>{formatDate(tx.date)}</TableCell>
                <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tx.description}
                </TableCell>
                <TableCell>
                  <Box 
                    onClick={(e) => handleCategoryClick(e, tx)}
                    sx={{ 
                      cursor: 'pointer', 
                      display: 'inline-flex', 
                      alignItems: 'center' 
                    }}
                  >
                    <Chip
                      label={tx.category || 'Uncategorized'}
                      size="small"
                      sx={{
                        bgcolor: getCategoryColor(tx.category, idx),
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    color: tx.transaction_type === 'income' ? 'success.main' : 'error.main',
                  }}
                >
                  {tx.transaction_type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(tx)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDelete(tx.id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  No transactions found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Category Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ style: { maxHeight: 400, width: 250 } }}
      >
        <MenuItem disabled>
          <Typography variant="body2">Change category for transaction</Typography>
        </MenuItem>
        {CATEGORIES.map((cat) => (
          <MenuItem 
            key={cat} 
            onClick={() => handleCategoryChange(cat)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              bgcolor: selectedTx?.category === cat ? 'action.selected' : 'inherit'
            }}
          >
            <Box sx={{ 
              width: 12, height: 12, borderRadius: '50%',
              bgcolor: getCategoryColor(cat, 0)
            }} />
            {cat}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
